import { IAnyModelType, SnapshotIn, types as t } from 'mobx-state-tree';
import * as React from 'react';

const StoreContext = React.createContext(null);
export const useStore = <S extends StoreInstances>(LocalStoreContext = StoreContext) =>
  React.useContext(LocalStoreContext) as S;

export interface StoreOption<T extends IAnyModelType> {
  model: T;
  defaultValue?: Partial<SnapshotIn<T>>;
}

export interface StoreInstances {
  [key: string]: StoreInstance;
}

export interface StoreInstance {}

interface StoreInfo {
  [key: string]: StoreOption<any>;
}

export interface StoreDeclaration {
  [key: string]: StoreOption<any>;
}

export const withContextStore = <P extends object>(storeInfo: StoreInfo, LocalStoreContext = StoreContext) => (
  Component: React.FunctionComponent<P>
): React.FunctionComponent<P> => (props) => {
  const rootStoreInstance = React.useMemo(() => {
    const subStoreInit = {};
    const modelProperties = Object.entries(storeInfo).reduce((p, [storeName, storeOption]) => {
      const { model, defaultValue = {} } = storeOption as StoreOption<any>;
      subStoreInit[storeName] = model.create(defaultValue);
      return {
        ...p,
        [storeName]: model,
      };
    }, {});
    const rootStore = t.model('RootStore', modelProperties);
    return rootStore.create(subStoreInit);
    // eslint-disable-next-line
  }, [storeInfo, LocalStoreContext]);
  return (
    <LocalStoreContext.Provider value={rootStoreInstance}>
      <Component {...props} />
    </LocalStoreContext.Provider>
  );
};
